import { useMemo, useState } from "react";
import { Loader2, Printer, Route } from "lucide-react";
import { toast } from "sonner";
import { Checkbox } from "@/components/ui/checkbox";
import { usePermissions } from "@/context/PermissionsContext";
import { useOrdersLuijtenVvz, type OrdersLuijtenRow } from "@/hooks/useOrdersLuijtenVvz";
import {
  ORDER_DECOPROF_STATUSES,
  printPackingSlipsBatch,
  type OrderDecoprofStatus,
  isMobileDevice,
} from "@/lib/ordersDecoprof";
import { useUpdateOrdersDecoprofStatus } from "@/hooks/useOrdersDecoprof";
import BadgeStatus3D from "./BadgeStatus3D";
import Card3D from "./Card3D";
import Button3D from "./Button3D";
import Filter3DDropdown from "./Filter3DDropdown";
import { generateWalkingRoutePdf } from "@/lib/walkingRoutePdf";


const STATUS_PILL_COLOR: Record<string, string> = {
  Nieuw: "bg-blue-500 text-white ring-blue-300/50",
  Uitgeprint: "bg-emerald-500 text-white ring-emerald-300/50",
  Aangemaakt: "bg-violet-500 text-white ring-violet-300/50",
};

const ORDER_DATE_FMT = new Intl.DateTimeFormat("nl-NL", {
  day: "numeric", month: "long", year: "numeric",
  hour: "2-digit", minute: "2-digit",
});
const formatOrderDateTime = (iso: string | null): string => {
  if (!iso) return "";
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return "";
  return ORDER_DATE_FMT.format(new Date(t)).replace(" om ", ", ");
};

const OrdersLuijtenVvzTab = () => {
  const { data: orders, isLoading, error } = useOrdersLuijtenVvz();
  const updateStatus = useUpdateOrdersDecoprofStatus();
  const { hasPermission } = usePermissions();

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [statusFilter, setStatusFilter] = useState<OrderDecoprofStatus[]>([]);
  const [search, setSearch] = useState("");
  const [isPrinting, setIsPrinting] = useState(false);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);

  const filtered = useMemo(() => {
    let list = orders ?? [];
    if (statusFilter.length > 0) list = list.filter((o) => statusFilter.includes(o.status));
    if (search.trim()) {
      const s = search.trim().toLowerCase();
      list = list.filter((o) => o.ordernumber.toLowerCase().includes(s));
    }
    return list;
  }, [orders, statusFilter, search]);

  const visibleOrdernumbers = useMemo(() => filtered.map((o) => o.ordernumber), [filtered]);
  const visibleSelectedCount = visibleOrdernumbers.filter((n) => selected.has(n)).length;
  const masterState: boolean | "indeterminate" =
    visibleOrdernumbers.length > 0 && visibleSelectedCount === visibleOrdernumbers.length
      ? true
      : visibleSelectedCount > 0 ? "indeterminate" : false;

  const toggleMaster = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (visibleSelectedCount === visibleOrdernumbers.length) {
        visibleOrdernumbers.forEach((n) => next.delete(n));
      } else {
        visibleOrdernumbers.forEach((n) => next.add(n));
      }
      return next;
    });
  };

  const toggleRow = (n: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(n) ? next.delete(n) : next.add(n);
      return next;
    });
  };

  const selectedRows: OrdersLuijtenRow[] = useMemo(
    () => (orders ?? []).filter((o) => selected.has(o.ordernumber)),
    [orders, selected],
  );

  const canRead = hasPermission("ordersLuijtenVvz", "read");
  const canWrite = hasPermission("ordersLuijtenVvz", "write");
  const hasSelection = selected.size >= 1;

  const handlePrint = async () => {
    const rows = selectedRows.filter((r) => r.orderid != null);
    if (rows.length === 0) {
      toast.error("Geen orders met geldig orderid geselecteerd");
      return;
    }
    let pre: Window | null = null;
    if (isMobileDevice()) {
      try { pre = window.open("about:blank", "_blank"); } catch { /* noop */ }
    }
    setIsPrinting(true);
    try {
      const ids = rows.map((r) => r.orderid as number);
      const res = await printPackingSlipsBatch(ids, pre);
      if (res.succeededOrderids.length > 0) {
        const successOrdernumbers = rows
          .filter((r) => res.succeededOrderids.includes(r.orderid as number))
          .map((r) => r.ordernumber);
        await updateStatus.mutateAsync({ ordernumbers: successOrdernumbers, status: "Uitgeprint" });
        setSelected((prev) => {
          const next = new Set(prev);
          successOrdernumbers.forEach((n) => next.delete(n));
          return next;
        });
        toast.success(`Pakbon gedownload — ${successOrdernumbers.length} order(s)`);
      }
      if (res.failedOrderids.length > 0) {
        toast.warning(`${res.failedOrderids.length} order(s) mislukt`);
      }
    } catch (e: any) {
      if (pre) try { pre.close(); } catch { /* noop */ }
      toast.error(`Pakbon mislukt: ${e?.message ?? e}`);
    } finally {
      setIsPrinting(false);
    }
  };

  const handleWalkingRoute = async () => {
    if (selectedRows.length === 0) {
      toast.error("Selecteer minimaal 1 order");
      return;
    }
    setIsGeneratingPdf(true);
    try {
      const projectId = import.meta.env.VITE_SUPABASE_PROJECT_ID;
      const url = `https://${projectId}.supabase.co/functions/v1/generate-luijten-walking-route`;
      const sessionToken = localStorage.getItem("auth_session_token");
      const res = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-session-token": sessionToken ?? "",
          apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
        },
        body: JSON.stringify({ ordernumbers: selectedRows.map((r) => r.ordernumber) }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err?.error || `HTTP ${res.status}`);
      }
      const data = await res.json();
      if (!data.items || data.items.length === 0) {
        toast.warning("Geen producten in voorraad (Goes) gevonden voor deze selectie");
        return;
      }
      generateWalkingRoutePdf({
        items: data.items,
        orderCount: selectedRows.length,
        generatedAt: new Date().toLocaleString("nl-NL"),
      });
      const colorCount = data.items.filter((i: any) => i.isColor).length;
      toast.success(
        `PDF gegenereerd — ${data.items.length} regel(s)${colorCount ? `, waarvan ${colorCount} KLEUR` : ""}`,
      );
    } catch (e: any) {
      toast.error(`PDF mislukt: ${e?.message ?? e}`);
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (error) {
    return (
      <Card3D className="p-6 text-center text-destructive">
        Fout bij laden: {(error as Error).message}
      </Card3D>
    );
  }

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3">
        <Filter3DDropdown
          label="Status"
          options={ORDER_DECOPROF_STATUSES as readonly string[]}
          values={statusFilter}
          onChange={(v) => setStatusFilter(v as OrderDecoprofStatus[])}
          pillColor={(v) => STATUS_PILL_COLOR[v] ?? ""}
        />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Zoek ordernummer..."
          className="px-3 py-2 rounded-lg border bg-background text-sm"
        />
        <div className="ml-auto flex items-center gap-2">
          <Button3D
            tone="neutral"
            onClick={handlePrint}
            disabled={!canRead || !hasSelection || isPrinting}
          >
            {isPrinting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Printer className="h-4 w-4" />}
            Print pakbon{hasSelection ? ` (${selected.size})` : ""}
          </Button3D>
          <Button3D
            tone="primary"
            onClick={handleWalkingRoute}
            disabled={!canWrite || !hasSelection || isGeneratingPdf}
          >
            {isGeneratingPdf ? <Loader2 className="h-4 w-4 animate-spin" /> : <Route className="h-4 w-4" />}
            Slimme looproute (PDF){hasSelection ? ` (${selected.size})` : ""}
          </Button3D>
        </div>
      </div>

      {/* Tabel-header */}
      <Card3D className="overflow-hidden">
        <div className="flex items-center gap-3 px-4 py-2 border-b bg-muted/30 text-xs font-medium uppercase text-muted-foreground">
          <Checkbox
            checked={masterState}
            onCheckedChange={toggleMaster}
            aria-label="Selecteer alle zichtbare orders"
          />
          <span className="w-32">Ordernummer</span>
          <span className="flex-1">Leverancier(s)</span>
          <span className="w-32">Status</span>
          <span className="w-44">Aangemaakt</span>
        </div>

        {filtered.length === 0 ? (
          <div className="px-4 py-12 text-center text-muted-foreground text-sm">
            Geen orders gevonden
          </div>
        ) : (
          <ul className="divide-y">
            {filtered.map((o) => (
              <li
                key={o.ordernumber}
                className="flex items-center gap-3 px-4 py-3 hover:bg-muted/40 cursor-pointer"
                onClick={() => toggleRow(o.ordernumber)}
              >
                <Checkbox
                  checked={selected.has(o.ordernumber)}
                  onCheckedChange={() => toggleRow(o.ordernumber)}
                  onClick={(e) => e.stopPropagation()}
                  aria-label={`Selecteer ${o.ordernumber}`}
                />
                <span className="w-32 font-mono text-sm font-medium">{o.ordernumber}</span>
                <span className="flex-1 text-sm">
                  {o.suppliers.join(", ")}
                </span>
                <span className="w-32">
                  <BadgeStatus3D status={o.status} />
                </span>
                <span className="w-44 text-xs text-muted-foreground">
                  {formatOrderDateTime(o.createdAt)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Card3D>

      <div className="text-xs text-muted-foreground">
        {filtered.length} order(s) zichtbaar — {selected.size} geselecteerd
      </div>
    </div>
  );
};

export default OrdersLuijtenVvzTab;
